import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"
import { Loader2 } from "lucide-react"

// Note: Radix UI Slot is optional but good for composition. 
// Since I haven't installed @radix-ui/react-slot yet, I'll stick to basic implementation first.
// I will simulate the "FashButton" spec.

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-white transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97] disabled:active:scale-100 font-sans",
  {
    variants: {
      variant: {
        default: "bg-primary text-white shadow-sm hover:bg-primary-hover active:bg-primary-hover",
        secondary: "bg-secondary text-heading hover:bg-secondary/70 active:bg-secondary/70",
        outline: "border border-gray-200 bg-white text-heading hover:bg-gray-50 active:bg-gray-100",
        ghost: "text-heading hover:bg-gray-100 active:bg-gray-200",
        link: "text-primary underline-offset-4 hover:text-primary-hover hover:underline",
        destructive: "bg-error text-white hover:bg-error/90 active:bg-error/90",
      },
      size: {
        default: "h-12 px-6 py-3 rounded-button", // 48px height
        sm: "h-9 rounded-lg px-3",
        lg: "h-14 rounded-button px-8 text-base",
        icon: "h-12 w-12 rounded-button",
      },
      fullWidth: {
        true: "w-full",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      fullWidth: false,
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, loading, children, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={props.disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {children}
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

